<template>
  <div>
    <h2 class="mb-5 text-lg font-medium text-gray-900 dark:text-white">Request Vacation</h2>
    <form @submit.prevent="submitRequest" class="max-w-sm mx-auto" enctype="multipart/form-data">
      <div :class="{ 'opacity-50 pointer-events-none': isSubmitting }">
        <DateRangePicker v-if="!isOtherType" @update:dateRange="handleDateRangeUpdate" :disabled="isSubmitting" :required="true" />

        <!-- Single date + hours for "Other" type -->
        <div v-if="isOtherType" class="mb-4">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Date</label>
          <input
            type="date"
            v-model="dateRange.start"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            :disabled="isSubmitting"
            required
          />
        </div>
        <div v-if="isOtherType" class="mb-4">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Hours</label>
          <input
            type="number"
            v-model="otherHours"
            min="1"
            max="8"
            placeholder="Number of hours (1-8)"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
            :disabled="isSubmitting"
            required
          />
        </div>
        <SelectField 
          id="type" 
          label="Type" 
          v-model="type" 
          :options="vacationTypes" 
          placeholder="Select type of leave" 
          :disabled="isSubmitting"
        />
        
        <TextArea
          id="note"
          :label="isOtherType ? 'Reason *' : 'Reason'"
          v-model="note"
          placeholder="Enter the reason for your leave"
          :disabled="isSubmitting"
          :required="isOtherType"
        />
        <InputField 
          id="attachments" 
          label="Attachments" 
          type="file" 
          v-model="attachments" 
          :disabled="isSubmitting"
        />
      </div>
      <Button 
        label="Submit Request" 
        :loading="isSubmitting"
        loading-label="Submitting Request..."
      />
    </form>
    <p v-if="message" :class="messageClass" class="mt-5 text-sm font-medium text-center">{{ message }}</p>

    <!-- Leave Breakdown Modal -->
    <!-- (Leave breakdown modal code remains the same) -->
  </div>
</template>


<script>
import { ref, computed, watch } from 'vue';
import InputField from './FormComponents/InputField';
import TextArea from './FormComponents/TextArea';
import Button from './FormComponents/Button';
import DateRangePicker from './FormComponents/DateRangePicker';
import SelectField from './FormComponents/SelectField';
import confetti from 'canvas-confetti';



export default {
  components: {
    InputField,
    TextArea,
    Button,
    DateRangePicker,
    SelectField,
  },
  props: {
    preselectedType: String,
  },
  setup(props) {
    const dateRange = ref({ start: '', end: '' });
    const type = ref(props.preselectedType || '');
    const note = ref('');
    const attachments = ref(null);
    const message = ref('');
    const messageClass = ref('');
    const showBreakdown = ref(false);
    const leaveBreakdown = ref([]);
    const overtimeHours = ref('');
    const otherHours = ref('');
    const isSubmitting = ref(false);

    const vacationTypes = [
      { value: 'vacation', text: 'Vacation - Use when taking time off', emoji: '🏖️' },
      { value: 'working_remotely', text: 'Working Remotely - Use when traveling and still working', emoji: '💻' },
      { value: 'sick_leave', text: 'Sick - Use when sick and can\'t work', emoji: '🤒' },
      { value: 'other', text: 'Other - Half day, late start, early off, etc.', emoji: '⏰' }
    ];

    const selectedEmoji = computed(() => {
      const selectedType = vacationTypes.find(v => v.value === type.value);
      return selectedType ? selectedType.emoji : '🎉';
    });

    const isOtherType = computed(() => type.value === 'other');

    const triggerConfetti = () => {
      const scalar = 2;
      const emojiShape = confetti.shapeFromText({ text: selectedEmoji.value, scalar });
      const defaults = {
        spread: 360,
        ticks: 60,
        gravity: 0,
        decay: 0.96,
        startVelocity: 20,
        shapes: [emojiShape],
        scalar,
      };

      function shoot() {
        confetti({
          ...defaults,
          particleCount: 30,
        });

        confetti({
          ...defaults,
          particleCount: 5,
          flat: true,
        });

        confetti({
          ...defaults,
          particleCount: 15,
          scalar: scalar / 2,
          shapes: ['circle'],
        });
      }

      setTimeout(shoot, 0);
      setTimeout(shoot, 100);
      setTimeout(shoot, 200);
    };

    const handleDateRangeUpdate = (newRange) => {
      console.log('handleDateRangeUpdate received:', newRange);
      dateRange.value = newRange;
      updateLeaveBreakdown();
    };

    const updateLeaveBreakdown = () => {
      if (!dateRange.value.start || !dateRange.value.end) {
        leaveBreakdown.value = [];
        return;
      }
      
      const start = new Date(dateRange.value.start);
      const end = new Date(dateRange.value.end);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        leaveBreakdown.value = [];
        return;
      }
      
      let current = new Date(start);
      leaveBreakdown.value = [];

      while (current <= end) {
        const isWeekend = current.getDay() === 0 || current.getDay() === 6;
        leaveBreakdown.value.push({
          date: current.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
          day: current.toLocaleDateString('en-US', { weekday: 'short' }),
          amount: isWeekend ? 0 : 1,
          isWeekend: isWeekend,
        });
        current.setDate(current.getDate() + 1);
      }

      showBreakdown.value = true;
    };

    const totalDays = computed(() => {
      return leaveBreakdown.value.reduce((total, day) => total + day.amount, 0);
    });

    const closeBreakdown = () => {
      showBreakdown.value = false;
    };

    const submitRequest = async () => {
      console.log('=== VACATION FORM SUBMIT START ===');
      console.log('Current isSubmitting state:', isSubmitting.value);
      
      // Prevent double submission
      if (isSubmitting.value) {
        console.log('Form already submitting, preventing double submission');
        return;
      }

      // Validate dates are selected
      console.log('Date validation - start:', dateRange.value.start, 'end:', dateRange.value.end);
      console.log('Date types - start type:', typeof dateRange.value.start, 'end type:', typeof dateRange.value.end);

      if (type.value === 'other') {
        // For "other" type, validate single date and hours
        if (!dateRange.value.start || dateRange.value.start === '' || dateRange.value.start === null) {
          message.value = 'Please select a date.';
          messageClass.value = 'text-red-500';
          return;
        }
        if (!otherHours.value || otherHours.value < 1 || otherHours.value > 8) {
          message.value = 'Please enter hours (1-8).';
          messageClass.value = 'text-red-500';
          return;
        }
      } else if (!dateRange.value.start || !dateRange.value.end ||
          dateRange.value.start === '' || dateRange.value.end === '' ||
          dateRange.value.start === null || dateRange.value.end === null) {
        message.value = 'Please select both start and end dates.';
        messageClass.value = 'text-red-500';
        console.log('Date validation failed');
        return;
      }

      // Validate type is selected
      if (!type.value) {
        message.value = 'Please select a leave type.';
        messageClass.value = 'text-red-500';
        return;
      }

      // Validate reason is provided for "other" type
      if (type.value === 'other' && (!note.value || note.value.trim() === '')) {
        message.value = 'Please provide a reason for "Other" type requests.';
        messageClass.value = 'text-red-500';
        return;
      }

      console.log('Submitting with dates:', dateRange);

      // Set loading state
      console.log('Setting isSubmitting to true');
      isSubmitting.value = true;
      console.log('isSubmitting after setting:', isSubmitting.value);
      message.value = '';
      messageClass.value = '';

      const requestData = new FormData();
      if (type.value === 'other') {
        // For "other" type, use single date for both start and end
        requestData.append('start_date', dateRange.value.start);
        requestData.append('end_date', dateRange.value.start);
        requestData.append('overtime_hours', otherHours.value);
      } else {
        requestData.append('start_date', dateRange.value.start);
        requestData.append('end_date', dateRange.value.end);
      }
      requestData.append('type', type.value);
      requestData.append('note', note.value);

      // Add overtime hours to the request if type is overtime
      if (type.value === 'overtime' && overtimeHours.value) {
        requestData.append('overtime_hours', overtimeHours.value);
      }

      watch(attachments, (newVal) => {
        console.log('Selected file:', newVal);
      });

      // Ensure the file is appended with the correct field name
      if (attachments.value instanceof File) {
        requestData.append('attachments', attachments.value); // 'attachments' matches the backend
      }

      console.log('attachments == ', attachments)

      try {
        const response = await fetch('/wp-json/vacation/v1/request', {
          method: 'POST',
          headers: {
            'X-WP-Nonce': vacationData.nonce,
          },
          body: requestData,
        });

        const responseData = await response.json();
        if (response.ok) {
          message.value = 'Vacation request submitted successfully.';
          messageClass.value = 'text-green-600 dark:text-green-400';
          console.log('Success:', responseData);

          triggerConfetti();

          setTimeout(() => {
            location.replace(location.href);
          }, 3000);
        } else {
          message.value = 'Failed to submit vacation request: ' + responseData.message;
          messageClass.value = 'text-red-600 dark:text-red-400';
          console.error('Server Error:', responseData);
        }
      } catch (error) {
        console.error('Network Error:', error);
        message.value = 'An error occurred. Please try again later.';
        messageClass.value = 'text-red-600 dark:text-red-400';
      } finally {
        // Always reset loading state
        console.log('Resetting isSubmitting to false');
        isSubmitting.value = false;
        console.log('=== VACATION FORM SUBMIT END ===');
      }
    };

    // Watch for changes in the preselectedType prop and update the type ref accordingly
    watch(() => props.preselectedType, (newVal) => {
      type.value = newVal;
    });

    return {
      dateRange,
      type,
      note,
      attachments,
      message,
      messageClass,
      submitRequest,
      handleDateRangeUpdate,
      vacationTypes,
      showBreakdown,
      leaveBreakdown,
      totalDays,
      closeBreakdown,
      triggerConfetti,
      overtimeHours,
      otherHours,
      isOtherType,
      isSubmitting,
    };
  },
};
</script>
